<template>
  <v-container fluid class="mb-12">
    <v-row>
      <v-col align="end" class="mt-3 mr-5">
        <v-btn :disabled="loadingFileMovements || (fileMovements && fileMovements.length === 0)" color="primary" class="mr-5" outlined @click=getFileMovements()>
          <v-icon :class="hasAuthorityUploadMovementMenu ? 'mr-2' : ''">mdi-refresh</v-icon>
          Atualizar
        </v-btn>

        <v-btn v-if="hasAuthorityUploadMovementMenu" color="primary" class="foreground-accent--text" @click="openModalUploadMovement">
          <v-icon class="mr-2" color="foreground-accent">mdi-file-upload</v-icon>
          Novo Upload
        </v-btn>
      </v-col>
      <v-col cols="12" class="py-0">
        <v-data-table
          class="default-table-height elevation-1"
          item-key="id"
          :headers="tableHeaders"
          :items="fileMovements"
          :loading="loadingFileMovements"
          :options.sync="options"
          :items-per-page="itemsPerPage"
          :page="page"
          :server-items-length="totalPages"
          :footer-props="{ itemsPerPageOptions: [25, 50, 75, 100], disablePagination: loadingFileMovements }"
          @update:options="changePage($event)"
          fixed-header
          hide-default-header
        >
          <template v-slot:header="{ props }">
            <thead class="v-data-table-header">
              <tr class="table-header">
                <th style="background-color: var(--v-muted-foreground-base) !important; text-align: start;" v-for="header in props.headers" :key="header.text">
                  <label class="label base-color">
                    {{ header.text }}
                  </label>
                </th>
              </tr>
            </thead>
          </template>
          <template v-slot:no-data>
            <span>Nenhum dado encontrado.</span>
          </template>
          <template v-slot:[`item.createdBy`]="{ item }">
            {{ item.createdBy ? formatter.formatToTitleCase(item.createdBy) : '-' }}
          </template>
          <template v-slot:[`item.fileName`]="{ item }">
            {{ item.fileName ? item.fileName : '-' }}
          </template>
          <template v-slot:[`item.helpdeskTickets`]="{ item }">
            <HelpdeskTicketsCell
              :helpdesk-tickets="item.helpdeskTickets"
              :helpdesk-id="item.helpdeskId"
            />
          </template>
          <template v-slot:[`item.receivingDate`]="{ item }">
            <span style="white-space: nowrap;">
              {{ item.receivingDate ? formatter.formatDateTimeZoneWithHours(item.receivingDate) : '-' }}
            </span>
          </template>
          <template v-slot:[`item.responsibleUserId`]="{ item }">
            <v-btn
              v-if="showResponsibleAssignmentButton(item)"
              color="primary"
              outlined
              small
              class="responsible-assign-button"
              @click.stop="fileAssignToMe(item)"
            >
              Atribuir para mim
            </v-btn>
            <OneLineCell v-else :text="resolveResponsibleUserDisplay(item)" />
          </template>
          <template v-slot:[`item.structureName`]="{ item }">
            <OneLineCell :text="resolveStructureNameDisplay(item)" />
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <span class="text--truncate">{{ item.status ? formatter.statusFormatterFileMovements(item.status) : '-' }}</span>
          </template>
          <template v-slot:[`item.processedElements`]="{ item }">
            <span
              :class="{
                'text--truncate': true,
                'font-weight-bold': wasValidated(item, 'processed')
              }"
              :style="`color: ${setStatusColor(item, 'processed')}`">
              {{ item.processedElements }}
            </span>
            /
            <span class="text--truncate foreground--text">
              {{ item.totalElements }}
            </span>
          </template>
          <template v-slot:[`item.canceledElements`]="{ item }">
            <span class="text--truncate foreground--text">
              {{ item.movementRecords }}
            </span>
            /
            <span
              :class="{
                'text--truncate': true,
                'font-weight-bold': wasValidated(item, 'canceled')
              }"
              :style="`color: ${setStatusColor(item, 'canceled')}`">
              {{ item.canceledMovementRecords }}
            </span>
          </template>
          <template v-slot:[`item.hasError`]="{ item }">
            <div v-if="item.hasError">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-btn color="primary" icon @click="openMovementErrorModal(item, 'protocol')">
                      <v-icon color="error" size="25"> fas fa-times </v-icon>
                    </v-btn>
                  </div>
                </template>
                <span> Erro de Movimentação </span>
              </v-tooltip>
            </div>
            <div v-else>
              <v-btn color="primary" icon disabled>
                <v-icon size="25"> fas fa-times </v-icon>
              </v-btn>
            </div>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-menu transition="slide-y-transition" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="base-color"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="countDocumentsByFileDataId(item);"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <TodoListActions :actions="getActionsFileMovementForItem(item)" />
            </v-menu>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <SnackbarCustomize
      ref="SnackbarCustomize"
    />
    <CancelMovementsModal
      ref="CancelMovementsModal"
      @filterMovementById="filterMovementById"
    />
    <UploadMovementsModal
      ref="UploadMovementsModal"
      @verifyCountDocuments="verifyCountDocuments"
    />
    <MovementErrorModal
      ref="MovementErrorModal"
      :movementError="propsMovementError"
      :movementErrorSourceId="propsMovementErrorSourceId"
      :insuredName="propsInsuredName"
      :loadingTableMovementError="propsLoadingTableMovementError"
      :tabError="propsTabError"
      :statusMovement="propsStatusMovement"
      @redirectMovements="filterMovementById"
      @getMovementByMovementRecordId="$emit('getMovementByMovementRecordId', $event)"
    />
    <MovementUploadModal
      ref="MovementUploadModal"
      :financialGroupsProps="financialGroups"
    />

    <CancelMovementBlockModal
      ref="CancelMovementBlockModal"
      :cancellationReasons="cancellationReasons"
      @showResult="showCancellationResult"
      :isMovementFlow="false"
      :fileDataId="fileMovementId"
      :isRHProtegido="isRHProtegido"
    />

    <CancelFileMovementModal
      ref="CancelFileMovementModal"
      @cancelMovements="cancelFileMovements"
      @updatePage="getFileMovements"
    />

    <LinkHelpdeskModal
      ref="LinkHelpdeskModal"
      @showMessage="showMessage"
      @updatePage="getFileMovements"
    />
  </v-container>
</template>

<script>
import FileDataService from '@/services-http/sdi/FileDataService';
import Formatters from '@/shared/formatters/formatters';
import TodoListActions from '@/components/TodoList/TodoListActions.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import DocumentService from '@/services-http/sdi/DocumentService';
import CancelMovementsModal from '@/components/DashboardMovementRecords/Common/CancelMovementsModal.vue'; /* add na common depois */
import UploadMovementsModal from '@/components/Beneficiary/Documents/UploadDocumentsModal.vue'; /* add na common depois */
import MovementErrorModal from '@/components/DashboardMovementRecords/Common/MovementErrorModal.vue';
import MovementErrorService from '@/services-http/sdi/MovementErrorsService';
import MovementRecordService from '@/services-http/sdi/MovementRecordService';
import Rules from '@/shared/validators/formRules';
import AuthorityManagementMixin from '@/shared/mixins/authorityManagement/authorityManagementMixin';
import VerifyRoutesMixin from '@/shared/mixins/routeManagement/verifyRoutesMixin';
import MovementUploadModal from '@/components/DashboardMovementRecords/Common/MovementUploadModal.vue';
import CancelMovementBlockModal from '@/components/Modals/CancelMovementBlockModal/CancelMovementBlockModal.vue';
import CancelFileMovementModal from '@/components/Modals/CancelFileMovementModal/CancelFileMovementModal.vue';
import LinkHelpdeskModal from '@/components/Modals/LinkHelpdeskModal/LinkHelpdeskModal.vue';
import MovementAssignmentService from '@/services-http/sdi/MovementAssignmentService';
import OneLineCell from '@/components/Default/OneLineCell.vue';
import HelpdeskTicketsCell from '@/components/Default/HelpdeskTicketsCell.vue';
import {
  hasResponsibleUser,
  insertBackofficeHeadersBeforeStatus,
  resolveResponsibleUserDisplay,
  resolveStructureNameDisplay,
} from '@/shared/utils/movement-attendance-display';

export default ({
  name: 'FileMovementsTable',

  components: {
    TodoListActions,
    SnackbarCustomize,
    CancelMovementsModal,
    UploadMovementsModal,
    MovementErrorModal,
    MovementUploadModal,
    CancelMovementBlockModal,
    CancelFileMovementModal,
    LinkHelpdeskModal,
    OneLineCell,
    HelpdeskTicketsCell,
  },

  data: () => ({
    loadingFileMovements: false,
    loadingCountDocuments: false,
    hasAuthorityUploadMovementMenu: false,
    propsLoadingTableMovementError: false,
    createdComponent: true,
    page: 1,
    totalPages: 0,
    itemsPerPage: 25,
    options: {},
    filterDocuments: {
      fileDataId: null,
      countDocuments: 0,
      documents: [],
    },
    propsStatusMovement: '',
    propsInsuredName: '',
    propsTabError: '',
    propsMovementErrorSourceId: null,
    insuranceCarrierIdWithError: '',
    isRHProtegido: false,
    propsMovementError: [],
    fileMovements: [],
    headers: [
      {
        text: 'Protocolo',
        value: 'protocolNumber',
        align: 'start',
        class: 'text--truncate',
      },
      {
        text: 'Nome do arquivo',
        value: 'fileName',
        align: 'start',
        class: 'text--truncate',
      },
      {
        text: 'Atendimento',
        value: 'helpdeskTickets',
        align: 'start',
        class: 'text--truncate',
        sortable: false,
      },
      {
        text: 'Criado por',
        value: 'createdBy',
        align: 'start',
        class: 'text--truncate',
      },
      {
        text: 'Recebimento',
        value: 'receivingDate',
        align: 'start',
        class: 'text--truncate',
      },
      {
        text: 'Grupo Financeiro',
        value: 'financialGroup',
        align: 'start',
        class: 'text--truncate',
      },
      {
        text: 'Status',
        value: 'status',
        align: 'start',
        class: 'text--truncate',
      },
      {
        text: 'Linhas Processadas',
        value: 'processedElements',
        align: 'start',
        class: 'text--truncate',
        sortable: false,
      },
      {
        text: 'Mov. Criadas/Canceladas',
        value: 'canceledElements',
        align: 'start',
        class: 'text--truncate',
        sortable: false,
      },
      {
        text: 'Erro',
        value: 'hasError',
        align: 'start',
        class: 'text--truncate',
      },
      {
        text: 'Ações',
        value: 'actions',
        align: 'start',
        class: 'text--truncate',
      },
    ],
    fileMovementId: null,
    cancellationReasons: [],
    allMovementIds: [],
  }),

  computed: {
    tableHeaders() {
      return insertBackofficeHeadersBeforeStatus(this.headers, this.isRHProtegido);
    },
  },

  mixins: [
    AuthorityManagementMixin,
    VerifyRoutesMixin,
  ],

  props: {
    filterFileMovements: { type: Object },
    financialGroups: { type: Array },
    resetFilterFileMovements: { type: Boolean },
    isManager: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    filterFileMovements: {
      deep: true,
      handler(value) {
        if (value && !this.resetFilterFileMovements) {
          this.page = 1;
          setTimeout(() => {
            this.getFileMovements();
          }, 250);
        }
      },
    },
  },

  created() {
    this.rule = new Rules();
    this.movementRecordService = new MovementRecordService();
    this.initializeServices();
  },

  async mounted() {
    this.hasAuthorityUploadMovementMenu = this.hasPermission('sdi_upload_arquivo_movimentacao');
    this.cancellationReasons = await this.movementRecordService.GetReasonCancellation();
  },

  methods: {
    showMessage(msgParam) {
      this.$refs.SnackbarCustomize.open(msgParam.type, msgParam.message);
    },
    resolveResponsibleUserDisplay(item) {
      return resolveResponsibleUserDisplay(item, this.formatter);
    },
    hasResponsibleUser(item) {
      return hasResponsibleUser(item);
    },
    resolveStructureNameDisplay(item) {
      return resolveStructureNameDisplay(item);
    },
    showCancellationResult() {
      this.$emit('getMovementByProtocolId', this.fileMovementId);
    },
    initializeServices() {
      this.fileDataService = new FileDataService();
      this.formatter = new Formatters();
      this.documentService = new DocumentService();
      this.movementErrorService = new MovementErrorService();
    },
    changePage(event) {
      if (!this.createdComponent) {
        this.itemsPerPage = event.itemsPerPage;
        this.page = event.page;
        this.getFileMovements();
      } else {
        this.createdComponent = false;
      }
    },
    async getFileMovements() {
      if (this.verifyParameterNumber(this.filterFileMovements.protocolNumber)) {
        this.loadingFileMovements = true;
        const filteredParams = this.getFilteredParams();
        const params = this.buildSearchParams(filteredParams);
        try {
          const response = await this.fileDataService.FindByParams(params);

          if (this.responseIsValid(response)) {
            this.updateFileMovements(response.data.content, response.data.totalElements);
          } else {
            this.fileMovements = [];
            this.totalPages = 0;
          }
        } catch (error) {
          this.$refs.SnackbarCustomize.open('error', 'Ocorreu um erro ao buscar as movimentações via arquivo');
        } finally {
          this.loadingFileMovements = false;
        }
      } else {
        this.$refs.SnackbarCustomize.open('error', 'Preencha os campos corretamente', 4000);
      }
    },
    getFilteredParams() {
      this.filterFileMovements.onlyFtpIntegration = this.filterFileMovements.onlyFtpIntegration || false;
      const filteredParams = Object.fromEntries(Object.entries(this.filterFileMovements).filter(([key, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value && (value !== '' && value !== null && typeof value !== 'undefined');
      }));

      if (filteredParams.responsibleUserIds) {
        filteredParams.includeUnassignedResponsible = false;
      }

      return filteredParams;
    },
    buildSearchParams(filteredParams) {
      return new URLSearchParams({
        ...filteredParams,
        page: this.page - 1,
        size: this.itemsPerPage,
        sort: 'id,desc',
      });
    },
    verifyParameterNumber(parameter) {
      if (String(parameter)) {
        if (parameter <= 0) {
          return false;
        }
        return true;
      }
      return true;
    },
    responseIsValid(response) {
      return response && response.data && response.data.content && response.data.content.length > 0;
    },
    updateFileMovements(content, totalElements) {
      this.fileMovements = content;
      this.totalPages = totalElements;
    },
    getActionsFileMovementForItem(item) {
      const userPermissions = {
        download: 'sdi_painel_movimentacoes_cadastrais_protocolo_download_arquivo',
        viewMovements: 'sdi_painel_movimentacoes_cadastrais_protocolo_visualizar_movimentacao',
        viewAllMovements: 'sdi_painel_movimentacoes_cadastrais_visualizar_movimentacao',
        upload: 'sdi_painel_movimentacoes_cadastrais_protocolo_upload_documentos',
        cancelMovement: 'sdi_painel_movimentacoes_cadastrais_protocolo_cancelar_movimentacoes',
      };

      const actions = [
        {
          label: 'Download',
          icon: 'fas fa-download',
          color: 'primary',
          condition: this.hasPermission(userPermissions.download),
          disabled: this.verifyFileWasUploaded(item.statusFile),
          handler: () => this.downloadFile(item.id, item.fileName),
        },
        {
          label: 'Ver Movimentações',
          icon: 'fas fa-eye',
          color: 'primary',
          condition: this.hasAllPermissions([userPermissions.viewMovements, userPermissions.viewAllMovements]),
          disabled: this.verifyFileWasUploaded(item.statusFile),
          handler: () => this.$emit('getMovementByProtocolId', item.protocolNumber),
        },
        {
          label: `Documentos ${!this.loadingCountDocuments && this.filterDocuments && this.filterDocuments.countDocuments ? `(${this.filterDocuments.countDocuments})` : !this.loadingCountDocuments ? '(0)' : ''}`,
          icon: 'fas fa-paperclip',
          color: 'primary',
          loadingCountDocuments: this.loadingCountDocuments,
          condition: this.hasPermission(userPermissions.upload),
          disabled: this.loadingCountDocuments,
          handler: () => this.openModalToAttachDocuments(item),
        },
        // {
        //   label: 'Cancelar movimentações',
        //   icon: 'fas fa-ban',
        //   color: 'primary',
        //   condition: this.hasAllPermissions([userPermissions.viewMovements, userPermissions.cancelMovement]) && this.verifyStatusFileProtocol(item.status),
        //   handler: () => this.cancelMovementsByBlocks(item),
        // },
        {
          label: 'Cancelar Arquivo',
          icon: 'fas fa-trash',
          color: 'primary',
          condition: this.hasAllPermissions([userPermissions.viewMovements, userPermissions.cancelMovement]) && this.verifyStatusFile(item.status) && item.protocolNumber !== null,
          handler: () => this.cancelMovementFileData(item),
        },
        {
          label: 'Incluir Nº Atendimento',
          icon: 'mdi mdi-numeric',
          color: 'primary',
          condition: this.hasPermissionToLinkHelpdeskIdInFileMovement(item),
          handler: () => this.requestLinkHelpdeskIdInFileMovement(item),
        },
        {
          label: 'Atribuir a mim',
          icon: 'mdi mdi-account-arrow-right',
          color: 'primary',
          condition: !this.isManager && !this.isRHProtegido && !this.hasResponsibleUser(item),
          handler: () => this.fileAssignToMe(item),
        },
      ];

      return actions;
    },
    hasPermissionToLinkHelpdeskIdInFileMovement(fileMovement) {
      const permissionToLink = this.hasPermission('sdi_vincular_atendimento_movimentacao');
      const statusReleasedToLinkHelpdeskId = ['FINISHED', 'PARTIAL'];

      return permissionToLink && statusReleasedToLinkHelpdeskId.includes(fileMovement.status);
    },
    requestLinkHelpdeskIdInFileMovement(fileMovement) {
      const linkService = async (fileMovementId, helpdeskId) => await this.fileDataService.LinkHelpdeskIdInFileMovement(fileMovementId, helpdeskId);

      this.$refs.LinkHelpdeskModal.open(fileMovement, fileMovement.id, linkService);
    },
    verifyStatusFileProtocol(statusItem) {
      return (statusItem && ['FINISHED', 'PARTIAL'].some((status) => status === statusItem));
    },
    verifyStatusFile(statusItem) {
      return (statusItem && ['COLLECTED', 'CONVERTED', 'PARTIAL', 'FINISHED', 'INVALID', 'REFUSED'].some((status) => status === statusItem));
    },
    verifyFileWasUploaded(statusFile) {
      return !statusFile;
    },
    openMovementErrorModal(item, tabError) {
      if (item) {
        this.propsTabError = tabError;
        this.propsStatusMovement = item.status;
        this.propsMovementErrorSourceId = item.id;
        if (this.propsStatusMovement !== 'TRANSFORMATION_ERROR') {
          this.propsLoadingTableMovementError = true;
          this.requestMovementErrors(item);
        } else {
          this.insuranceCarrierIdWithError = item.id;
          this.$refs.MovementErrorModal.openModal();
        }
      }
    },
    requestMovementErrors(item) {
      const query = `?fileDataId=${item.id}`;
      this.movementErrorService.FindAllByFilters(query).then(async (response) => {
        if (response && response.data && response.data.length > 0) {
          this.propsMovementError = await response.data;
        }
        this.propsLoadingTableMovementError = false;
        this.$refs.MovementErrorModal.openModal();
      }).catch(() => {
        this.propsLoadingTableMovementError = false;
      });
    },
    downloadFile(id, filename) {
      this.fileDataService.downloadFile(id).then((response) => {
        if (response) {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute('download', `${filename}`);
          document.body.appendChild(fileLink);

          fileLink.click();
          this.$refs.SnackbarCustomize.open('success', 'Download realizado com sucesso.', 3000);
        }
      }).catch(() => {
        this.$refs.SnackbarCustomize.open('error', 'Ocorreu um erro ao tentar efetuar o download.');
      });
    },
    async countDocumentsByFileDataId(item) {
      if (!item.protocolNumber) {
        this.loadingCountDocuments = false;
      } else {
        const fileDataId = item.protocolNumber;
        if ((fileDataId && this.filterDocuments && (this.filterDocuments.fileDataId === null || this.filterDocuments.fileDataId !== fileDataId)) || (this.countUploadOrDeleteDocuments !== this.filterDocuments.countDocuments)) {
          this.loadingCountDocuments = true;
          this.filterDocuments.fileDataId = null;
          this.filterDocuments.countDocuments = 0;

          const queryString = `?fileDataId=${fileDataId}`;
          await this.documentService.CountDocumentByFilters(queryString).then((response) => {
            this.filterDocuments.fileDataId = fileDataId;
            if (response) {
              this.filterDocuments.countDocuments = response.data;
              this.countUploadOrDeleteDocuments = response.data;
            }
          }).catch(() => {
            this.loadingCountDocuments = false;
            this.$refs.SnackbarCustomize.open('error', 'Ocorreu um erro ao buscar os documentos');
          }).finally(() => {
            this.loadingCountDocuments = false;
          });
        }
      }
    },
    async openModalToAttachDocuments(item) {
      if (this.filterDocuments && this.filterDocuments.fileDataId) {
        this.loadingDocuments = true;
        const queryString = `?fileDataId=${this.filterDocuments.fileDataId}`;
        await this.documentService.FindDocumentByFilters(queryString).then((response) => {
          if (response && response.data) {
            this.filterDocuments.documents = response.data;
            this.filterDocuments.protocolNumber = item.protocolNumber;
            this.$refs.UploadMovementsModal.open(this.filterDocuments, 'PROTOCOL');
          }
        }).catch(() => {
          this.loadingDocuments = false;
          this.$refs.SnackbarCustomize.open('error', 'Ocorreu um erro ao buscar os documentos');
        }).finally(() => {
          this.loadingDocuments = false;
        });
      }
    },
    verifyCountDocuments(event) {
      this.countUploadOrDeleteDocuments = event;
    },
    cancelMovementsByBlocks(item) {
      const props = {
        ...item,
        isCancelMovements: true,
      };
      this.$refs.CancelMovementsModal.open(props);
    },
    cancelMovementFileData(item) {
      this.fileMovementId = item.id;
      this.$refs.CancelFileMovementModal.open(item);
    },
    async findAllIds() {
      const params = new URLSearchParams({ protocolNumber: this.fileMovementId });

      const movementRecordService = new MovementRecordService();

      try {
        const response = await movementRecordService.FindAllIds(params);
        const data = Array.isArray(response.data) ? response.data : [];

        this.allMovementIds = data.some((item) => item && Array.isArray(item.movementIds))
          ? data.reduce((ids, structure) => ids.concat(structure.movementIds || []), [])
          : data;
      } catch (error) {
        this.setSnackbarCustomize('error', 'Ocorreu um erro ao buscar as movimentações');
      } finally {
      }
    },
    async cancelFileMovements(item) {
      this.fileMovementId = item.id;
      this.$emit('updateFilterFileMovement', ('protocolNumber', item.id));
      await this.findAllIds();
      this.$refs.CancelMovementBlockModal.open(this.allMovementIds);
    },
    async filterMovementById(id, status) {
      this.$emit('getMovementByProtocolId', id, status);
    },
    openModalUploadMovement() {
      this.$refs.MovementUploadModal.open();
    },
    wasValidated(item, title) {
      switch (title) {
        case 'processed':
          if (item.processedElements === 0 && item.totalElements === 0) return false;
          if (item.processedElements === item.totalElements) return true;
          if (item.processedElements > 0) return true;
          if (item.processedElements === 0) return true;
          break;
        case 'canceled':
          if (item.canceledMovementRecords === 0 && item.movementRecords === 0) return false;
          if (item.canceledMovementRecords > 0) return true;
          return false;
        default:
          return false;
      }
    },
    setStatusColor(item, title) {
      switch (title) {
        case 'processed':
          if (item.processedElements === 0 && item.totalElements === 0) return 'var(--v-foreground-base) !important';
          if (item.processedElements === item.totalElements) return 'var(--v-success-base) !important';
          if (item.processedElements > 0) return 'var(--v-warning-base) !important';
          if (item.processedElements === 0) return 'var(--v-error-base) !important';
          break;
        case 'canceled':
          if (item.canceledMovementRecords === 0 && item.movementRecords === 0) return 'var(--v-foreground-base) !important';
          if (item.canceledMovementRecords > 0) return 'var(--v-error-base)';
          return 'var(--v-foreground-base) !important';
        default:
          return 'var(--v-foreground-base) !important';
      }
    },

    async fileAssignToMe(item) {
      if (this.isManager || this.isRHProtegido || this.hasResponsibleUser(item)) return;

      const assignmentService = new MovementAssignmentService();

      try {
        await assignmentService.fileAssignToMe(item.id);
        this.$refs.SnackbarCustomize.open('success', 'Arquivo atribuído com sucesso');
        this.getFileMovements();
      } catch {
        this.$refs.SnackbarCustomize.open('error', 'Erro ao atribuir arquivo');
      }
    },
    showResponsibleAssignmentButton(item) {
      return !this.isManager && !this.isRHProtegido && !this.hasResponsibleUser(item);
    },
  },
});
</script>

<style scoped>
.responsible-assign-button {
  min-width: 136px;
}
</style>
